import type { CrawlerType } from '@dcc/rules/domain/character.ts';
import type { CombatSelection, SecondCombatChoice } from '@dcc/rules/rules/startingCombat.ts';
import { automaticCombatSkillId, meetsAttackSpellPrerequisite } from '@dcc/rules/rules/startingCombat.ts';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';

/**
 * The second combat Skill (C&C pp.12-13). Only legal options can be picked; the
 * Attack Spells stay visible but disabled, with the reason, when Intelligence
 * is too low. Ported from the desktop creator.
 */
export function CombatStep(props: {
  crawlerType: CrawlerType;
  intelligence: number;
  takenSkillIds: Set<string>;
  selection: CombatSelection;
  onChange: (selection: CombatSelection) => void;
  onBackToStats: () => void;
}) {
  const content = contentRegistry.startingCombat;
  const automaticId = automaticCombatSkillId(content, props.crawlerType);
  const automatic = automaticId ? contentRegistry.getSkill(automaticId) : undefined;
  const spellsAllowed = meetsAttackSpellPrerequisite(content, props.intelligence);
  const chosen = props.selection.second;

  function optionRow(kind: SecondCombatChoice['kind'], skillId: string, note?: string) {
    const skill = contentRegistry.getSkill(skillId);
    const isTaken = props.takenSkillIds.has(skillId);
    const disabled = isTaken || (kind === 'spell' && !spellsAllowed);
    const selected = chosen !== null && chosen.kind === kind && chosen.skillId === skillId;
    return (
      <button
        key={`${kind}-${skillId}`}
        className={selected ? 'dcc-creator-option is-selected' : 'dcc-creator-option'}
        disabled={disabled}
        onClick={() => props.onChange({ second: { kind, skillId } as SecondCombatChoice })}
      >
        <span>{skill ? skill.name : skillId}</span>
        <span className="dcc-creator-meta">
          {isTaken ? 'already taken from a background' : (note ?? `Rank ${content.secondSkillRank}`)}
        </span>
      </button>
    );
  }

  return (
    <section className="dcc-creator-card">
      <h2>Starting combat Skills</h2>
      <p>
        Automatic: <strong>{automatic ? automatic.name : automaticId}</strong> at Rank{' '}
        {content.automatic.find((a) => a.crawlerType === props.crawlerType)?.rank}{' '}
        <span className="dcc-creator-help">(C&amp;C p.12)</span>
      </p>
      <p className="dcc-creator-help">Every crawler also starts with the Heal Spell at Rank 1 (C&amp;C p.15).</p>

      <h3>Second combat Skill: Weapon</h3>
      <div className="dcc-creator-options">{content.weaponSkillIds.map((id) => optionRow('weapon', id))}</div>

      <h3>Or an Attack Spell</h3>
      {!spellsAllowed && (
        <div className="dcc-creator-warn">
          A starting Attack Spell needs Intelligence {content.attackSpellMinimumIntelligence} or higher
          (C&amp;C p.13). This crawler has {props.intelligence}.{' '}
          <button className="dcc-creator-link" onClick={props.onBackToStats}>Change Stats</button>
        </div>
      )}
      <div className="dcc-creator-options">
        {content.attackSpellIds.map((id) => {
          const skill = contentRegistry.getSkill(id);
          return optionRow('spell', id, skill?.manaCost != null ? `${skill.manaCost} Mana` : undefined);
        })}
      </div>
      {spellsAllowed && <p className="dcc-creator-help">Taking a Spell also grants {content.attackSpellGrant}.</p>}

      <h3>Or Hand-to-Hand</h3>
      <div className="dcc-creator-options">
        {content.handToHand.map((pair) => {
          const effect = contentRegistry.getSkill(pair.damageEffectSkillId);
          return optionRow('hand-to-hand', pair.skillId, `with ${effect ? effect.name : pair.damageEffectSkillId}`);
        })}
      </div>
      <p className="dcc-creator-help">Entering unarmed also grants {content.handToHandGrant}.</p>
    </section>
  );
}
